import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import { useInboxDrawer } from 'context/InboxDrawerContext'
import { buildUrlWithEntityContext } from 'utils/EntityNavigationUtils'

/**
 * Cleans up malformed URLs by removing quotes, escaped characters, etc.
 */
const cleanUrl = (url: string): string => {
  if (!url) return url

  // Remove quotes and escaped quotes
  let cleaned = url.replace(/^["']|["']$/g, '').replace(/\\%22/g, '')

  // Remove malformed protocol patterns like https://"
  cleaned = cleaned.replace(/^https?:\/\/["']/g, 'https://')

  // Remove trailing quotes or escaped characters
  cleaned = cleaned.replace(/["']$/, '').replace(/\\%22$/, '')

  return cleaned.trim()
}

/**
 * Converts an absolute URL to a relative path, always extracting the path part
 * and ignoring external domains to ensure links use the current domain
 */
const convertToRelativePath = (url: string): string => {
  if (!url) return url

  // Clean up malformed URLs first
  const cleanedUrl = cleanUrl(url)

  // Already relative URLs (starting with /, #, or ?)
  if (
    cleanedUrl.startsWith('/') ||
    cleanedUrl.startsWith('#') ||
    cleanedUrl.startsWith('?')
  ) {
    return cleanedUrl
  }

  // Always try to extract the path part first, regardless of domain
  // This ensures we use the current domain instead of redirecting to external domains
  const pathMatch = cleanedUrl.match(/(\/[^?#\s"']*)(\?[^#\s"']*)?(#.*)?/)

  if (pathMatch && pathMatch[0]) {
    return pathMatch[0]
  }

  // Check if it's an absolute URL on the same domain
  try {
    // Try to parse as absolute URL
    let linkUrl: URL

    if (cleanedUrl.startsWith('http://') || cleanedUrl.startsWith('https://')) {
      linkUrl = new URL(cleanedUrl)

      // If it's NOT the same domain, extract path anyway to use current domain
      if (linkUrl.origin !== window.location.origin) {
        return linkUrl.pathname + linkUrl.search + linkUrl.hash
      }
    } else {
      // If it doesn't start with http/https, try with current origin
      linkUrl = new URL(cleanedUrl, window.location.origin)
    }

    // Convert to relative path
    return linkUrl.pathname + linkUrl.search + linkUrl.hash
  } catch {
    // If URL parsing fails, try to extract path manually
    // Look for patterns like /dashboard?blockId=...
    const fallbackPathMatch = cleanedUrl.match(
      /(\/[^?#\s"']*)(\?[^#\s"']*)?(#.*)?/
    )

    if (fallbackPathMatch && fallbackPathMatch[0]) {
      return fallbackPathMatch[0]
    }
  }

  // If all else fails, return cleaned URL
  return cleanedUrl
}

/**
 * Determines if a URL should be processed (has a path we can extract)
 * We treat URLs with paths as processable so we can extract the path
 * and use it with the current domain
 */
const isInternalLink = (url: string): boolean => {
  if (!url) return false

  // Clean up malformed URLs first
  const cleanedUrl = cleanUrl(url)

  // Relative URLs (starting with /, #, or ?) - always process these
  if (
    cleanedUrl.startsWith('/') ||
    cleanedUrl.startsWith('#') ||
    cleanedUrl.startsWith('?')
  ) {
    return true
  }

  // If it contains a path pattern (starts with /), treat as processable
  // This allows us to extract paths from URLs with any domain
  if (cleanedUrl.includes('/')) {
    // Check if there's a path pattern we can extract
    const hasPathPattern = /\/[^?#\s"']/.test(cleanedUrl)

    if (hasPathPattern) {
      return true
    }
  }

  // Check if it's the same domain
  try {
    let linkUrl: URL

    if (cleanedUrl.startsWith('http://') || cleanedUrl.startsWith('https://')) {
      linkUrl = new URL(cleanedUrl)

      // If same domain, process it
      if (linkUrl.origin === window.location.origin) {
        return true
      }

      // If different domain but has a path, still process it to extract the path
      if (linkUrl.pathname && linkUrl.pathname !== '/') {
        return true
      }
    } else {
      linkUrl = new URL(cleanedUrl, window.location.origin)

      return linkUrl.origin === window.location.origin
    }
  } catch {
    // If URL parsing fails, check if it looks like it has a path
    if (cleanedUrl.includes('/')) {
      return true
    }
  }

  // Default: don't process external URLs without paths
  return false
}

// Helper function to extract all entity IDs from URL search params
// Extracts all parameters ending with 'Id' (e.g., contractId, meterId, opportunityId, orderId)
const extractEntityIdsFromSearch = (
  searchString: string
): Record<string, string> => {
  const params = new URLSearchParams(searchString)
  const entityIds: Record<string, string> = {}

  params.forEach((value, key) => {
    // Extract all parameters ending with 'Id'
    if (key.endsWith('Id') && value) {
      entityIds[key] = value
    }
  })

  return entityIds
}

// Helper function to convert entity schema to URL parameter name
// Pattern: "contract" -> "contractId", "meter" -> "meterId", etc.
const schemaToUrlParam = (schema: string): string => {
  // Convert schema name to camelCase URL param (e.g., "contract" -> "contractId")
  return `${schema.charAt(0).toLowerCase()}${schema.slice(1)}Id`
}

// Helper function to build URL with entity context
// Extracts all entity IDs from current URL and adds them to the new URL
const buildUrlWithContext = (
  relativePath: string,
  currentSearch: string,
  relatedEntities?: Array<{ entity_id: string; entity_schema: string }>
): string => {
  // Extract pathname and search params from relative path
  const [pathname, search] = relativePath.split('?')
  const additionalParams: Record<string, string> = {}
  const linkHasBlockIds =
    search?.includes('block-ids') ||
    search?.includes('blockId') ||
    search?.includes('block_id')

  if (search) {
    const searchParams = new URLSearchParams(search)

    searchParams.forEach((value, key) => {
      // Keep block-ids if it's in the link itself (for highlighting)
      // Otherwise exclude it
      if (key === 'block-ids' || key === 'blockId' || key === 'block_id') {
        if (linkHasBlockIds) {
          additionalParams[key] = value
        }
        // If link doesn't have block-ids, don't add it (will be removed from current URL)
      } else {
        additionalParams[key] = value
      }
    })
  }

  // Use window.location.search as the source of truth for current entity IDs
  // This ensures we always get the actual current URL's search params
  const actualSearch = currentSearch || window.location.search

  // Extract ALL entity IDs from current URL search params
  // This includes contractId, meterId, opportunityId, orderId, and any other *Id params
  const currentEntityIds = extractEntityIdsFromSearch(actualSearch)

  // Remove block-ids and blockId from currentEntityIds if link doesn't have it
  // (If link has block-ids, we want to keep it for highlighting)
  if (!linkHasBlockIds) {
    delete currentEntityIds['block-ids']
    delete currentEntityIds['blockId']
    delete currentEntityIds['block_id']
  }

  // Extract entity IDs from related_entities array
  // Convert schema names to URL param names (e.g., "contract" -> "contractId")
  const relatedEntityIds: Record<string, string> = {}

  if (relatedEntities && Array.isArray(relatedEntities)) {
    relatedEntities.forEach((entity) => {
      if (entity.entity_id && entity.entity_schema) {
        const urlParam = schemaToUrlParam(entity.entity_schema)

        relatedEntityIds[urlParam] = entity.entity_id
      }
    })
  }

  // Merge all entity IDs: current URL params, related entities, and additional params from link
  // Priority: additionalParams > relatedEntityIds > currentEntityIds
  const allParams = {
    ...currentEntityIds,
    ...relatedEntityIds,
    ...additionalParams
  }

  // Remove block-ids and blockId parameters from final params only if link doesn't have it
  // (If link has block-ids, we want to keep it for highlighting)
  if (!linkHasBlockIds) {
    delete allParams['block-ids']
    delete allParams['blockId']
    delete allParams['block_id']
  }

  // Use buildUrlWithEntityContext to handle localStorage and other edge cases
  // Pass all entity IDs as additionalParams, and undefined for allowedSchemas to include all
  // Use actualSearch to ensure we're using the real current search params
  return buildUrlWithEntityContext(pathname, allParams, undefined, actualSearch)
}

type CustomLinkPluginProps = {
  relatedEntities?: Array<{ entity_id: string; entity_schema: string }>
}

const CustomLinkPlugin = ({ relatedEntities }: CustomLinkPluginProps) => {
  const [editor] = useLexicalComposerContext()
  const history = useHistory()
  const location = useLocation()
  const { closeDrawer } = useInboxDrawer()

  useEffect(() => {
    return editor.registerRootListener((rootElement) => {
      if (!rootElement) return null

      // Convert absolute URLs to relative paths with entity context in link href attributes
      const convertLinkHrefs = () => {
        const links = rootElement.querySelectorAll('a[href]')

        links.forEach((link) => {
          const href = link.getAttribute('href')

          if (href && isInternalLink(href)) {
            const relativePath = convertToRelativePath(href)
            // Use window.location.search to ensure we get the actual current URL's search params
            const urlWithContext = buildUrlWithContext(
              relativePath,
              window.location.search,
              relatedEntities
            )

            if (urlWithContext !== href) {
              link.setAttribute('href', urlWithContext)
            }
          }
        })
      }

      // Convert links on initial load
      convertLinkHrefs()

      // Watch for new links added dynamically
      const observer = new MutationObserver(convertLinkHrefs)

      observer.observe(rootElement, {
        childList: true,
        subtree: true
      })

      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement
        const linkElement = target.closest('a')

        if (linkElement) {
          const href = linkElement.getAttribute('href')

          if (href && isInternalLink(href)) {
            // Only handle internal links - prevent default and use React Router
            event.preventDefault()
            event.stopPropagation()

            // Convert absolute URLs to relative paths
            const relativePath = convertToRelativePath(href)

            // Build URL with entity context
            // Use window.location.search to ensure we get the actual current URL's search params
            const urlWithContext = buildUrlWithContext(
              relativePath,
              window.location.search,
              relatedEntities
            )

            // Close the inbox drawer if it's open
            closeDrawer()

            if (
              urlWithContext.startsWith('#') ||
              urlWithContext.startsWith('?')
            ) {
              // For hash or query links, update current location
              window.location.hash = urlWithContext.startsWith('#')
                ? urlWithContext
                : ''
              if (urlWithContext.startsWith('?')) {
                history.push({ search: urlWithContext })
              }
            } else {
              // Navigate using React Router for path changes with entity context
              const [finalPathname, finalSearch] = urlWithContext.split('?')

              history.push({
                pathname: finalPathname,
                search: finalSearch ? `?${finalSearch}` : ''
              })
            }

            return false
          }

          // External links: do nothing, let them behave normally
        }

        return undefined
      }

      // Use capture phase to ensure we catch the event before it bubbles
      rootElement.addEventListener('click', handleClick, true)

      return () => {
        observer.disconnect()
        rootElement.removeEventListener('click', handleClick, true)
      }
    })
  }, [editor, history, location.search, closeDrawer, relatedEntities])

  return null
}

export default CustomLinkPlugin
